import React, { useState, useEffect } from 'react';
import { X, Images, Download, Users, Activity, RefreshCw, Calendar } from 'lucide-react';
import { supabase } from '../services/supabase';

interface Stats {
  totalBatches: number;
  totalImages: number;
  totalDownloads: number;
  uniqueUsers: number;
  recentBatches: { date: string; batches: number; images: number }[];
}

interface AdminStatsProps {
  onClose: () => void;
}

type TimeRange = '7d' | '30d' | '90d' | 'all';

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

const AdminStats: React.FC<AdminStatsProps> = ({ onClose }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const fetchStats = async (range: TimeRange) => {
    setLoading(true);
    try {
      const now = new Date();
      let since: string | null = null;
      if (range === '7d') since = new Date(now.getTime() - 7 * 86400000).toISOString();
      else if (range === '30d') since = new Date(now.getTime() - 30 * 86400000).toISOString();
      else if (range === '90d') since = new Date(now.getTime() - 90 * 86400000).toISOString();

      let batchQuery = supabase
        .from('event_logs')
        .select('metadata, created_at, user_id')
        .eq('event_type', 'batch_processed');
      if (since) batchQuery = batchQuery.gte('created_at', since);
      const { data: batchRows } = await batchQuery;

      let dlQuery = supabase
        .from('event_logs')
        .select('id', { count: 'exact', head: true })
        .eq('event_type', 'batch_zip_downloaded');
      if (since) dlQuery = dlQuery.gte('created_at', since);
      const { count: totalDownloads } = await dlQuery;

      const rows = batchRows || [];
      const totalBatches = rows.length;
      const totalImages = rows.reduce((sum, r) => {
        const count = (r.metadata as any)?.imageCount;
        return sum + (typeof count === 'number' ? count : 0);
      }, 0);

      const userIds = new Set(rows.map((r) => r.user_id).filter(Boolean));

      // Daily breakdown (last 14 days max)
      const dailyMap = new Map<string, { batches: number; images: number }>();
      rows.forEach((r) => {
        const day = r.created_at.slice(0, 10);
        const existing = dailyMap.get(day) || { batches: 0, images: 0 };
        existing.batches++;
        const count = (r.metadata as any)?.imageCount;
        existing.images += typeof count === 'number' ? count : 0;
        dailyMap.set(day, existing);
      });
      const recentBatches = Array.from(dailyMap.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .slice(0, 14)
        .map(([date, data]) => ({ date, ...data }));

      setStats({
        totalBatches,
        totalImages,
        totalDownloads: totalDownloads || 0,
        uniqueUsers: userIds.size,
        recentBatches,
      });
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(timeRange);
  }, [timeRange]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Activity size={20} className="text-emerald-400" />
            <h2 className="text-lg font-bold text-slate-100">Tools Usage Stats</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchStats(timeRange)}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5">
          {/* Time range selector */}
          <div className="flex items-center gap-1.5 mb-5 bg-slate-800/50 p-1 rounded-xl">
            {TIME_RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setTimeRange(r.value)}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  timeRange === r.value
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <RefreshCw size={20} className="animate-spin mr-2" />
              Loading stats...
            </div>
          ) : stats ? (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <StatCard icon={Activity} label="Batch Runs" value={stats.totalBatches} color="emerald" />
                <StatCard icon={Images} label="Images Processed" value={stats.totalImages} color="cyan" />
                <StatCard icon={Download} label="Downloads" value={stats.totalDownloads} color="violet" />
                <StatCard icon={Users} label="Unique Users" value={stats.uniqueUsers} color="amber" />
              </div>

              {/* Daily breakdown */}
              {stats.recentBatches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={14} className="text-slate-500" />
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Daily Breakdown</h3>
                  </div>
                  <div className="space-y-1 max-h-[240px] overflow-y-auto pr-1">
                    {stats.recentBatches.map((day) => (
                      <div
                        key={day.date}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/40 hover:bg-slate-800/70 transition-colors"
                      >
                        <span className="text-sm text-slate-300 font-mono">{day.date}</span>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-emerald-400">{day.batches} batch{day.batches !== 1 ? 'es' : ''}</span>
                          <span className="text-cyan-400">{day.images} img{day.images !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-slate-500 py-8">Failed to load stats.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const COLORS: Record<string, string> = {
  emerald: 'bg-emerald-900/30 text-emerald-400',
  cyan: 'bg-cyan-900/30 text-cyan-400',
  violet: 'bg-violet-900/30 text-violet-400',
  amber: 'bg-amber-900/30 text-amber-400',
};

const StatCard: React.FC<{
  icon: React.FC<{ size?: number; className?: string }>;
  label: string;
  value: number;
  color: string;
}> = ({ icon: Icon, label, value, color }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${COLORS[color]}`}>
        <Icon size={16} />
      </div>
    </div>
    <div className="text-2xl font-bold text-slate-100 tabular-nums">{value.toLocaleString()}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);

export default AdminStats;
